import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DirectoryRefresh } from '@/components/ContactsTable';
import { ChevronDown, Search } from 'lucide-react';
import { DateStamp, QueryError, PageIntro, Person, RecordTable, Status, count } from '@/workspace/ui';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Registrations (/stealth) — design: the prototype's Registrations directory. StealthWebinar registrations, newest
// first (the API returns the newest 1,000); a linked row opens its contact.
export default function StealthPage({ embedded = false, onSelectContact, totalRegistrations }) {
  const Wrap = embedded ? 'div' : 'section';

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');   // all | Linked | Unlinked
  const { data: regs = [], isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: ['stealth'],
    queryFn: () => fetch(`${BACKEND_URL}/api/stealth`).then(r => { if (!r.ok) throw new Error(); return r.json(); }),
    refetchInterval: 15_000,
  });

  const rows = useMemo(() => regs.filter(r => {
    if (filter !== 'all' && (r.contact_id ? 'Linked' : 'Unlinked') !== filter) return false;
    if (!search) return true;
    return [r.name, r.email, r.phone, ...(r.tags || [])].join(' ').toLowerCase().includes(search.trim().toLowerCase());
  }), [regs, search, filter]);
  const withFb = regs.filter(r => r.has_fbclid).length;
  const withPhone = regs.filter(r => r.phone).length;

  const columns = [
    { label: 'Registrant', key: 'person', className: 'sp-primary', render: r => <Person name={r.name} email={r.email} /> },
    { label: 'Phone', key: 'phone', render: r => r.phone || '—' },
    { label: 'Registered', key: 'date', render: r => <DateStamp value={r.registered_at} /> },
    { label: 'Facebook click', key: 'facebook', render: r => <Status tone={r.has_fbclid ? 'blue' : 'quiet'}>{r.has_fbclid ? 'Tracked' : 'No click'}</Status> },
    { label: 'Tags', key: 'tags', render: r => (r.tags?.length ? <span className="sp-tags">{r.tags.map(t => <Status key={t} tone="blue">{t}</Status>)}</span> : '—') },
    { label: 'Connection', key: 'status', render: r => <Status tone={r.contact_id ? 'green' : 'amber'}>{r.contact_id ? 'Linked' : 'Unlinked'}</Status> },
  ];

  return <>
    {!embedded && <PageIntro title="Registrations" />}
    <Wrap className={embedded ? undefined : 'panel'} aria-label="Registrations list">
      <div className="table-toolbar">
        <label className="search-input"><Search size={17} aria-hidden="true" /><span className="sr-only">Search registrations</span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search records" /></label>
        <label className="select-wrap"><span className="sr-only">Filter registrations</span>
          <select value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="all">All connections</option><option value="Linked">Linked</option><option value="Unlinked">Unlinked</option>
          </select><ChevronDown size={15} aria-hidden="true" /></label>
        {(search || filter !== 'all') && <button type="button" className="text-btn" onClick={() => { setSearch(''); setFilter('all'); }}>Clear filters</button>}
        <span className="toolbar-end"><DirectoryRefresh onRefresh={refetch} loading={isFetching} label="Refresh registrations" /></span>
      </div>
      {isError ? <QueryError onRetry={refetch} /> : isLoading ? <div className="empty-state">Loading registrations…</div> : (
        <RecordTable rows={rows} columns={columns} label="Registrations"
          onOpen={r => r.contact_id && onSelectContact?.(r.contact_id)}
          isRowInteractive={r => !!r.contact_id && !!onSelectContact}
          openName={r => `View contact for ${r.name || r.email || 'anonymous registrant'}`}
          emptyText={regs.length ? 'No registrations match these filters.' : 'No registrations yet. Connect your webinar platform using the Registrations webhook in Connections.'}
          foot={<>
            <span>Newest {count(regs.length)}{totalRegistrations != null && ` of ${count(totalRegistrations)}`} registrations · {count(withFb)} from Facebook · {count(withPhone)} with phone</span>
            <span>Unlinked registrations have no contact to open yet</span>
          </>} />
      )}
    </Wrap>
  </>;
}
